import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import ProductBadge from '../../components/base/ProductBadge';
import ProductButton from '../../components/base/ProductButton';

export default function ProduitCoeurRubanRougeLivrePane() {
  const [quantity, setQuantity] = useState(1);
  const [showNotification, setShowNotification] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const product = {
    id: 'coeur-ruban-rouge-livre-plie',
    name: "Livre plié 'Cœur ruban rouge' – Pièce unique",
    price: 30,
    image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/3fa376e8e8807f31eaa1afa0fce6cfd3.png',
    stock: 1,
    maxQuantity: 1,
    category: 'Saint-Valentin'
  };

  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: quantity,
      image: product.image
    });
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    setTimeout(() => {
      window.REACT_APP_NAVIGATE?.('/panier');
    }, 500);
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <title>Livre plié cœur ruban rouge – Pièce unique artisanale | Les Carnets de Lolotte</title>
      <meta name="description" content="Livre plié avec cœur en relief et ruban rouge. Création artisanale unique en livre recyclé, idéale en cadeau romantique et symbolique." />
      <meta name="keywords" content="livre plié cœur, cadeau romantique, decoration saint valentin, coeur en relief, creation artisanale, piece unique" />
      
      {/* Open Graph */}
      <meta property="og:title" content="Livre plié cœur ruban rouge – Pièce unique artisanale | Les Carnets de Lolotte" />
      <meta property="og:description" content="Livre plié avec cœur en relief et ruban rouge. Création artisanale unique en livre recyclé, idéale en cadeau romantique et symbolique." />
      <meta property="og:image" content={product.image} />
      <meta property="og:type" content="product" />
      
      {/* JSON-LD Product Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "Product",
          "name": product.name,
          "image": product.image,
          "description": "Livre plié avec cœur en relief et ruban rouge. Création artisanale unique en livre recyclé, idéale en cadeau romantique et symbolique.",
          "brand": {
            "@type": "Brand",
            "name": "Les Carnets de Lolotte"
          },
          "offers": {
            "@type": "Offer",
            "url": "https://lescarnetsdelolotte.fr/produit-coeur-ruban-rouge-livre-plie",
            "priceCurrency": "EUR",
            "price": product.price,
            "availability": "https://schema.org/InStock",
            "itemCondition": "https://schema.org/NewCondition"
          }
        })}
      </script>

      <div className="min-h-screen bg-brand-rose-50">
        {/* Notification harmonisée */}
        {showNotification && (
          <div className="fixed top-24 left-1/2 transform -translate-x-1/2 bg-brand-rose-500 text-white px-8 py-4 rounded-full shadow-2xl z-50 animate-bounce">
            <div className="flex items-center gap-3">
              <i className="ri-check-line text-2xl"></i>
              <span className="font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Ajouté au panier !</span>
            </div>
          </div>
        )}

        {/* Breadcrumb */}
        <div className="bg-brand-beige-100 backdrop-blur-sm border-b border-brand-rose-200">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-brand-text-secondary hover:text-brand-rose-500 transition-colors cursor-pointer" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Accueil
              </Link>
              <i className="ri-arrow-right-s-line text-brand-rose-400"></i>
              <Link to="/boutique" className="text-brand-text-secondary hover:text-brand-rose-500 transition-colors cursor-pointer" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Boutique
              </Link>
              <i className="ri-arrow-right-s-line text-brand-rose-400"></i>
              <span className="text-brand-rose-600 font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>Livre plié "Cœur ruban rouge"</span>
            </div>
          </div>
        </div>

        {/* Product Content */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image Section */}
            <div className="space-y-6">
              <div className="relative bg-white rounded-3xl overflow-hidden shadow-lg border border-brand-rose-200">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-auto object-cover"
                />
                
                {/* Badge unique seulement - suppression "Avant-première" */}
                <div className="absolute top-6 left-6">
                  <ProductBadge type="unique">
                    ✨ Pièce unique
                  </ProductBadge>
                </div>
              </div>
            </div>

            {/* Info Section */}
            <div className="space-y-8">
              {/* Title & Price harmonisés */}
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold text-brand-text-primary mb-4 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Livre plié "Cœur ruban rouge"
                </h1>
                <p className="text-xl text-brand-text-secondary mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>Pièce unique</p>
                <div className="flex items-baseline gap-4">
                  <span className="text-5xl font-bold text-brand-rose-600" style={{ fontFamily: 'Playfair Display, serif' }}>{product.price}€</span>
                </div>
              </div>

              {/* Short Description */}
              <div className="bg-brand-beige-100 rounded-2xl p-6 border border-brand-rose-200">
                <p className="text-brand-text-primary leading-relaxed text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Un cœur sculpté dans les pages d'un livre, sublimé par un ruban rouge et une touche florale. Une pièce unique, romantique et symbolique.
                </p>
              </div>

              {/* Add to Cart harmonisé */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center border-2 border-brand-rose-400 rounded-xl overflow-hidden bg-white">
                    <ProductButton
                      variant="quantity"
                      size="sm"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      icon="ri-subtract-line"
                    >
                    </ProductButton>
                    <span className="px-8 py-4 font-bold text-xl text-brand-text-primary border-x-2 border-brand-rose-300" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {quantity}
                    </span>
                    <ProductButton
                      variant="quantity"
                      size="sm"
                      onClick={() => setQuantity(Math.min(product.maxQuantity, quantity + 1))}
                      disabled={quantity >= product.maxQuantity}
                      icon="ri-add-line"
                    >
                    </ProductButton>
                  </div>
                  <span className="text-sm text-brand-text-secondary" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Stock : {product.stock} disponible
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <ProductButton
                    variant="primary"
                    size="lg"
                    onClick={handleAddToCart}
                    icon="ri-shopping-cart-line"
                  >
                    Ajouter au panier
                  </ProductButton>
                  <ProductButton
                    variant="secondary"
                    size="lg"
                    onClick={handleBuyNow}
                    icon="ri-flashlight-line"
                  >
                    Acheter
                  </ProductButton>
                </div>
              </div>

              {/* Features harmonisées */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-brand-rose-200">
                <h3 className="text-xl font-bold text-brand-text-primary mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <i className="ri-information-line text-brand-rose-500"></i>
                  Caractéristiques
                </h3>
                <div className="space-y-3 text-brand-text-primary" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-brand-rose-500 mt-1"></i>
                    <div>
                      <span className="font-semibold">Type :</span> Sculpture en livre plié
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-brand-rose-500 mt-1"></i>
                    <div>
                      <span className="font-semibold">Motif :</span> Grand cœur en relief / effet "cœur enveloppé"
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-brand-rose-500 mt-1"></i>
                    <div>
                      <span className="font-semibold">Décor :</span> Ruban rouge, feuille verte, fleur papier rouge
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-brand-rose-500 mt-1"></i>
                    <div>
                      <span className="font-semibold">Pièce :</span> Unique (stock 1)
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-brand-rose-500 mt-1"></i>
                    <div>
                      <span className="font-semibold">Dimensions :</span> à renseigner
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div className="mt-16 space-y-12">
            {/* Main Description */}
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl border border-pink-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-heart-3-fill text-pink-600"></i>
                Un cœur sculpté avec passion
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
                <p>
                  Cette création artisanale en livre plié révèle un <strong>grand cœur en relief</strong> au centre, avec un effet "enveloppe" très graphique.
                </p>
                <p>
                  Elle est accompagnée d'un <strong>ruban rouge</strong>, d'une <strong>feuille verte</strong> et d'une <strong>fleur papier rouge</strong>, pour une composition pleine de douceur et de passion.
                </p>
                <div className="grid md:grid-cols-2 gap-6 mt-8">
                  <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-hand-heart-line text-3xl text-pink-600"></i>
                      <h3 className="font-bold text-lg text-gray-900">Fait main avec soin</h3>
                    </div>
                    <p className="text-gray-700">Chaque pli est réalisé à la main avec précision et amour.</p>
                  </div>
                  <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-recycle-line text-3xl text-green-600"></i>
                      <h3 className="font-bold text-lg text-gray-900">Livre recyclé</h3>
                    </div>
                    <p className="text-gray-700">Upcycling : une seconde vie pleine de sens pour un livre ancien.</p>
                  </div>
                  <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-gift-line text-3xl text-purple-600"></i>
                      <h3 className="font-bold text-lg text-gray-900">Emballage soigné</h3>
                    </div>
                    <p className="text-gray-700">Prêt à offrir dans un emballage élégant et protecteur.</p>
                  </div>
                  <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
                    <div className="flex items-center gap-3 mb-3">
                      <i className="ri-heart-2-line text-3xl text-rose-600"></i>
                      <h3 className="font-bold text-lg text-gray-900">Cadeau romantique</h3>
                    </div>
                    <p className="text-gray-700">Idéal pour Saint-Valentin, anniversaire, déclaration, mariage.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Why We Love It */}
            <div className="bg-gradient-to-br from-pink-100 to-purple-100 rounded-3xl p-8 lg:p-12 shadow-xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <i className="ri-star-fill text-yellow-500"></i>
                Pourquoi on l'aime
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-vip-crown-line text-3xl text-white"></i>
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">Création unique</h3>
                  <p className="text-gray-700">Une seule pièce au monde, impossible à reproduire à l'identique.</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-heart-pulse-line text-3xl text-white"></i>
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">Symbolique forte</h3>
                  <p className="text-gray-700">Le cœur et le ruban rouge expriment l'amour et la passion.</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-leaf-line text-3xl text-white"></i>
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 mb-2">Cadeau responsable</h3>
                  <p className="text-gray-700">Artisanat local et recyclage : un geste éco-responsable.</p>
                </div>
              </div>
            </div>

            {/* Key Points */}
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl border border-pink-100">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <i className="ri-checkbox-circle-line text-pink-600"></i>
                Points forts
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-vip-crown-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-1">Pièce unique (1 exemplaire)</h3>
                    <p className="text-gray-600">Vous possédez la seule et unique création de ce modèle.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-3-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-1">Cœur en relief sculpté à la main</h3>
                    <p className="text-gray-600">Technique de pliage minutieuse pour un effet 3D saisissant.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-gift-2-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-1">Ruban rouge + décor floral</h3>
                    <p className="text-gray-600">Finitions soignées avec ruban, feuille et fleur en papier.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-purple-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-sparkling-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-1">Déco romantique et poétique</h3>
                    <p className="text-gray-600">Parfait pour sublimer un intérieur avec douceur.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-add-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 mb-1">Cadeau symbolique</h3>
                    <p className="text-gray-600">Exprime l'amour, la tendresse et l'attention portée à l'autre.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}